import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { clearCachedDashboardStats } from '../services/dashboardStatsCache';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const isAbortError = (error) =>
  error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const fetchAbortRef = useRef(null);

  const cancelPendingFetch = useCallback(() => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
      fetchAbortRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    cancelPendingFetch();
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    clearCachedDashboardStats();
    delete api.defaults.headers.common['Authorization'];
  }, [cancelPendingFetch]);

  const fetchUser = useCallback(async (tokenForRequest) => {
    const activeToken = tokenForRequest ?? localStorage.getItem('token');
    if (!activeToken) {
      setLoading(false);
      return;
    }

    cancelPendingFetch();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    try {
      const response = await api.get('/user', { signal: controller.signal });
      if (localStorage.getItem('token') !== activeToken) {
        return;
      }
      setUser(response.data);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      console.error('Error fetching user:', error);
      if (localStorage.getItem('token') !== activeToken) {
        return;
      }
      if (error.response?.status === 401 || !error.response) {
        logout();
      }
    } finally {
      const isCurrent = fetchAbortRef.current === controller;
      if (isCurrent) {
        fetchAbortRef.current = null;
      }
      if (isCurrent || localStorage.getItem('token') !== activeToken) {
        setLoading(false);
      }
    }
  }, [cancelPendingFetch, logout]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      fetchUser(token);
    } else {
      cancelPendingFetch();
      setUser(null);
      setLoading(false);
    }
  }, [token, user, fetchUser, cancelPendingFetch]);

  const login = async (email, password) => {
    try {
      cancelPendingFetch();
      let response;
      try {
        response = await api.post('/login', { email, password }, { timeout: 60000 });
      } catch (firstError) {
        const noServer = !firstError.response && (firstError.request || firstError.code === 'ECONNABORTED');
        if (!noServer) {
          throw firstError;
        }
        await new Promise((resolve) => setTimeout(resolve, 800));
        response = await api.post('/login', { email, password }, { timeout: 60000 });
      }
      const { user: loggedInUser, token: newToken } = response.data;

      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setUser(loggedInUser);
      setToken(newToken);
      setLoading(false);

      return { success: true, user: loggedInUser };
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Erreur de connexion';
      
      if (error.response) {
        // Erreur de validation Laravel
        if (error.response.data?.errors) {
          const errors = error.response.data.errors;
          errorMessage = Object.values(errors).flat().join(', ') || errorMessage;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = 'Impossible de contacter le serveur. Vérifiez que le serveur Laravel est démarré.';
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user || (!!token && loading),
    isAdmin: user?.role === 'admin',
    isOperateur: user?.role === 'operateur',
    isMaitresse: user?.role === 'maitresse',
    isDonateur: user?.role === 'donateur',
    canManageOrphelins: user?.role === 'admin',
    canManageActualites: user?.role === 'admin',
    canManageDonateurs: user?.role === 'admin',
    canLaunchPrelevement: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

